import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  Client,
  ClientAvecStats,
  ClientInput,
  ClientListQuery,
  ClientUpdate,
  Commande,
  Facture,
  Vente,
} from '@sirajoptic/shared';
import { api } from '@/lib/api-client';
import { cleDashboard } from '@/features/dashboard/api/dashboard.api';

export type FicheClient = {
  client: Client;
  commandes: Commande[];
  factures: Facture[];
  /** Ventes au comptoir rattachées à cette fiche, les annulées exclues. */
  ventes: Vente[];
  derniere_ordonnance: Commande | null;
  /** Dernières mesures connues — pas forcément la commande ci-dessus. */
  dernieres_mesures: Commande | null;
  stats: {
    nombre_achats: number;
    total_depense: number;
    reste_du: number;
    dernier_achat: string | null;
    /**
     * Départ du délai AMO : la dernière ordonnance datée, à défaut la dernière
     * commande. Le délai part du dernier **équipement** — une vente au
     * comptoir ne le repousse pas. L'échéance se calcule ici, côté navigateur,
     * puisque c'est lui qui sait quel jour on est.
     */
    amo_depart: string | null;
    /** Vrai quand `amo_depart` est une date de commande, faute d'ordonnance. */
    amo_depart_estime: boolean;
  };
};

export const clesClients = {
  tous: ['clients'] as const,
  liste: (q: Partial<ClientListQuery>) => ['clients', 'liste', q] as const,
  fiche: (id: string) => ['clients', 'fiche', id] as const,
  autocomplete: (q: string) => ['clients', 'autocomplete', q] as const,
};

export function useClients(q: Partial<ClientListQuery>) {
  return useQuery({
    queryKey: clesClients.liste(q),
    queryFn: () => api.liste<ClientAvecStats>('/clients', q),
    placeholderData: (precedent) => precedent,
  });
}

export function useFicheClient(id: string | undefined) {
  return useQuery({
    queryKey: clesClients.fiche(id ?? ''),
    queryFn: () => api.get<FicheClient>(`/clients/${id}`),
    enabled: Boolean(id),
  });
}

/**
 * Autocomplétion du formulaire de commande. La requête ne part qu'à partir de
 * deux caractères : en dessous, la liste n'apprend rien à l'utilisateur.
 */
export function useAutocompleteClients(terme: string) {
  const nettoye = terme.trim();
  return useQuery({
    queryKey: clesClients.autocomplete(nettoye),
    queryFn: () => api.get<Client[]>('/clients', { q: nettoye, autocomplete: 1 }),
    enabled: nettoye.length >= 2,
    staleTime: 30_000,
  });
}

function useInvalider() {
  const client = useQueryClient();
  return () => {
    void client.invalidateQueries({ queryKey: clesClients.tous });
    void client.invalidateQueries({ queryKey: ['commandes'] });
    // Une fusion réattache aussi les ventes au comptoir : le registre change.
    void client.invalidateQueries({ queryKey: ['ventes'] });
    void client.invalidateQueries({ queryKey: cleDashboard });
  };
}

export function useCreerClient() {
  const invalider = useInvalider();
  return useMutation({
    mutationFn: (input: ClientInput) => api.post<Client>('/clients', input),
    onSuccess: invalider,
  });
}

export function useModifierClient() {
  const invalider = useInvalider();
  return useMutation({
    mutationFn: ({ id, patch }: { id: string; patch: ClientUpdate }) =>
      api.patch<Client>(`/clients/${id}`, patch),
    onSuccess: invalider,
  });
}

export function useSupprimerClient() {
  const invalider = useInvalider();
  return useMutation({
    mutationFn: (id: string) => api.delete<{ supprime: boolean }>(`/clients/${id}`),
    onSuccess: invalider,
  });
}

/** Fusion manuelle des doublons (règle 12). */
export function useFusionnerClients() {
  const invalider = useInvalider();
  return useMutation({
    mutationFn: ({ garde, mergeIds }: { garde: string; mergeIds: string[] }) =>
      api.post<{ client: Client; commandes: number; factures: number; ventes: number }>(
        `/clients/${garde}/fusionner`,
        { mergeIds },
      ),
    onSuccess: invalider,
  });
}
